/** True when the value is a non-null object, including a Firestore Timestamp. */
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/** A finite number, or null when the value is not one. */
const finiteNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const readDateMillis = (value: Date): number | null => finiteNumber(value.getTime());

const readIsoMillis = (value: string): number | null => {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Millis for a CMS picker `Date` or leftover Firestore timestamp-like value.
 * Nested array dates often arrive as `{seconds,nanoseconds}`, a Date, an ISO
 * string, or epoch millis. Runtime visibility uses `publish_date_milis` only.
 */
export const readCmsTimestampMillis = (value: unknown): number | null => {
  if (value instanceof Date) {
    return readDateMillis(value);
  }

  if (typeof value === "number") {
    return finiteNumber(value);
  }

  if (typeof value === "string") {
    return readIsoMillis(value);
  }

  if (!isRecord(value)) {
    return null;
  }

  const toMillis: unknown = value.toMillis;

  if (typeof toMillis === "function") {
    return finiteNumber(toMillis.call(value));
  }

  const getTime: unknown = value.getTime;

  if (typeof getTime === "function") {
    return finiteNumber(getTime.call(value));
  }

  const seconds = finiteNumber(value.seconds) ?? finiteNumber(value._seconds);

  if (seconds === null) {
    return null;
  }

  const nanoseconds = finiteNumber(value.nanoseconds) ?? finiteNumber(value._nanoseconds) ?? 0;
  return seconds * 1000 + nanoseconds / 1e6;
};
